/**
 * ShareBackdrop — 공유 화면 배경 (SC-020).
 *
 * Balsa UI 의 절차적 그라디언트(`studio-background`)를 그대로 쓴다. 셰이더·수치는
 * 손대지 않는다 — 받은 설정(src/backgrounds/studio-background.ts)이 곧 그 배경의
 * 정의다.
 *
 * ── 카드 색을 **미세하게** 섞는다 ────────────────────────────────────
 * 배경이 카드마다 똑같으면 공유 이미지가 전부 같은 그림이 된다. 그렇다고 카드
 * 색으로 화면을 물들이면(앞서 0.35 로 해 봤다) 초록 화면·주황 화면이 되어 카드가
 * 배경에 묻힌다.
 *
 * 그래서 설정의 **다섯 색 중 가운데 두 자리만** 카드 색 쪽으로 조금 당긴다.
 * 가장 어두운 두 색(#050506·#0E0F1A)과 가장 밝은 색(#D7D8DA)은 그대로 둔다 —
 * 바탕의 깊이와 하이라이트가 카드마다 흔들리면 브랜드 톤이 흐트러진다.
 * 결과는 "같은 배경인데 카드에 따라 공기 색이 다르다" 정도다.
 *
 * ⚠ three.js 를 쓰는 컴포넌트라 **지연 로딩**한다. 공유 화면은 앱에서 가장
 *   늦게 닿는 화면인데, 그 배경 때문에 첫 화면 번들이 무거워질 이유가 없다.
 * ⚠ 캔버스는 장식이다 — aria-hidden · pointer-events: none 을 유지한다.
 *   (컴포넌트가 스스로 붙이지만, 감싼 상자에서도 한 번 더 못박는다.)
 * ⚠ 모션 저감(prefers-reduced-motion)은 컴포넌트가 이미 지킨다 — 여기서 끄지 않는다.
 *
 * ⚠ **WebGL 이 없거나 끊기면 아무것도 그리지 않는다.** 컨텍스트를 잃은 캔버스는
 *   흰 판으로 남아 화면을 통째로 덮는다(실측: 임베디드 브라우저에서
 *   "THREE.WebGLRenderer: Context Lost" 와 함께 배경이 하얗게 떴다).
 *   그럴 때는 캔버스를 걷어 아래 CSS 레이어(바탕·카드 색·도트)가 그대로 보이게
 *   둔다 — 배경이 조금 심심해질 뿐 화면이 망가지지는 않는다.
 */
import { Suspense, lazy, useEffect, useMemo, useRef, useState } from "react";
import { studioBackground } from "../../backgrounds/studio-background";
import styles from "./ShareBackdrop.module.css";

const GradientBackground = lazy(() =>
  import("../ui/GradientBackground").then((m) => ({ default: m.GradientBackground })),
);

/** #rrggbb → [r,g,b] */
function toRgb(hex: string): [number, number, number] {
  const v = hex.replace("#", "");
  return [
    Number.parseInt(v.slice(0, 2), 16),
    Number.parseInt(v.slice(2, 4), 16),
    Number.parseInt(v.slice(4, 6), 16),
  ];
}

/** a 를 b 쪽으로 t 만큼 당긴다 (0 = a 그대로) */
function mix(a: string, b: string, t: number): string {
  const [ar, ag, ab] = toRgb(a);
  const [br, bg, bb] = toRgb(b);
  const ch = (x: number, y: number) =>
    Math.round(x + (y - x) * t)
      .toString(16)
      .padStart(2, "0");
  return `#${ch(ar, br)}${ch(ag, bg)}${ch(ab, bb)}`;
}

/**
 * 화면에 실제로 쓰는 팔레트 — **받은 설정보다 한 단 밝다** (2026-08-25).
 *
 * 받은 다섯 색은 넷이 거의 검정이다(#050506 밝기 0.02 · #0E0F1A 0.06 ·
 * #2B2D31 0.17). 그런데 디더 점의 농도는 화면 밝기에서 나온다:
 *   shade = floor(fieldLuminance(field) * steps + threshold) / steps
 * 밝기가 0.05 언저리면 이 값이 거의 모든 픽셀에서 0 이라 잉크가 얹힐 자리가
 * 없다 — 점 크기·단계·색 모드를 아무리 만져도 안 보이던 이유다.
 *
 * 그래서 **가운데 대역을 0.24~0.36 으로 끌어올린다.** 그 구간에서 위 식이
 * 0 과 0.5, 0.5 와 1 사이를 오가며 점이 또렷하게 찍힌다.
 * ⚠ 색상각은 그대로 남색이다. 밝아졌어도 '어두운 배경' 으로 읽혀야 한다 —
 *   회색으로 올리면 화면이 뿌옇게 뜬다.
 * ⚠ 첫 자리(가장 어두운 색)와 끝(하이라이트)은 원본에 가깝게 둔다. 깊이와
 *   반짝임은 원래 설정의 것이다.
 */
const PALETTE: string[] = [
  "#0B0D14",
  // 가운데 두 색을 한 단 더 올렸다 (2026-08-25, 실기기 확인 뒤).
  // 0.13/0.24 에서도 점이 덜 보였다 — 디더가 또렷해지는 구간(0.3~0.5)까지 민다.
  "#2A3045", // 0.13 → 0.21
  "#454D6A", // 0.24 → 0.36
  "#8890A8",
  "#D7D8DA",
];

/** 카드 색 키 → 섞어 넣을 대표 색. CARD_GRADIENTS 의 밝은 쪽 끝 색이다. */
const ACCENT_BY_COLOR_KEY: Record<string, string> = {
  fire: "#ff9a56",
  thunder: "#ffe259",
  psychic: "#c471ed",
  steel: "#cfd9df",
  normal: "#e8d9c4",
  grass: "#96e6a1",
};

interface ShareBackdropProps {
  /** 카드 아트워크 색 키 (dummyCards.colorKey). 실 카탈로그에는 없어 옵셔널 */
  colorKey?: string;
}

/** 이 기기가 WebGL 을 그릴 수 있는가 */
function canRenderWebGl(): boolean {
  if (typeof document === "undefined") return false;
  try {
    const probe = document.createElement("canvas");
    return Boolean(probe.getContext("webgl2") ?? probe.getContext("webgl"));
  } catch {
    return false;
  }
}

export default function ShareBackdrop({ colorKey }: ShareBackdropProps = {}) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [failed, setFailed] = useState(() => !canRenderWebGl());

  /**
   * 컨텍스트를 잃으면 캔버스를 걷는다.
   * ⚠ webglcontextlost 는 **버블링하지 않는다** — 감싼 상자에 걸면 안 잡힌다.
   *   캔버스가 붙은 뒤에 그 요소에 직접 건다.
   */
  useEffect(() => {
    if (failed) return;
    const host = hostRef.current;
    if (!host) return;
    let canvas: HTMLCanvasElement | null = null;
    const onLost = () => setFailed(true);

    /**
     * 이벤트만으로는 못 잡는 경우가 있다 — 캔버스가 붙기 **전에** 컨텍스트가
     * 끊기면 우리 리스너는 그 순간을 지나쳐 흰 판만 남는다(실측). 그래서 붙인 뒤
     * 잠깐 동안 상태를 직접 물어본다. 같은 인자로 getContext 를 부르면 렌더러가
     * 쓰는 그 컨텍스트가 그대로 돌아온다(새로 만들지 않는다).
     */
    const check = () => {
      canvas = host.querySelector("canvas");
      if (!canvas) return;
      canvas.addEventListener("webglcontextlost", onLost);
      const gl =
        (canvas.getContext("webgl2") as WebGL2RenderingContext | null) ??
        (canvas.getContext("webgl") as WebGLRenderingContext | null);
      if (!gl || gl.isContextLost()) setFailed(true);
    };
    const timers = [0, 300, 1200].map((ms) => window.setTimeout(check, ms));
    return () => {
      timers.forEach((id) => window.clearTimeout(id));
      canvas?.removeEventListener("webglcontextlost", onLost);
    };
  }, [failed]);

  const config = useMemo(() => {
    const base = { ...studioBackground, colors: [...PALETTE] };
    const accent = colorKey ? ACCENT_BY_COLOR_KEY[colorKey] : undefined;
    if (!accent) return base;
    const colors = [...PALETTE];
    /**
     * 가운데 세 자리를 카드 색 쪽으로 당긴다 (2026-08-25 조정: 0.22/0.14 →
     * 0.5/0.34/0.18). 실기기에서 "효과가 너무 미미하다" 는 확인을 받았다 —
     * 다섯 색 중 둘만, 그것도 20% 남짓 당기면 어두운 바탕에서는 차이가 거의
     * 안 보인다.
     * ⚠ 0(가장 어두운 바탕)은 여전히 건드리지 않는다. 거기까지 물들이면 글자
     *   뒤가 밝아져 대비가 무너진다.
     */
    colors[1] = mix(colors[1], accent, 0.18);
    colors[2] = mix(colors[2], accent, 0.5);
    colors[3] = mix(colors[3], accent, 0.34);
    return { ...base, colors };
  }, [colorKey]);

  if (failed) return null;

  return (
    <div className={styles.backdrop} ref={hostRef} aria-hidden>
      {/* 셰이더가 뜨기 전에는 아무것도 안 그린다 — 아래 바탕색이 그대로 보인다 */}
      <Suspense fallback={null}>
        {/* ⚠ 세기 조정은 **prop 으로** 덮는다 (2026-08-25). 생성된 설정 파일
            (src/backgrounds/studio-background.ts)은 CLI 가 관리하는 산출물이라
            거기 손대면 `balsa update` 때 덮이거나 충돌로 남는다.
            받은 값 대비: 대비 1.18→1.5 · 밝기 -0.08→0.02 · 결 1.12/1.2→1.35/1.45.
            ⚠ 밝기는 조금만 올린다. 이 배경 위에 흰 글자가 얹히므로 바탕이 밝아지면
              대비가 먼저 무너진다 — 세기는 **대비와 결**로 낸다.

            ── 2026-08-25 2·3차 조정 (실기기 확인) ───────────────────────
            · 속도 0.24 → 0.38 → 0.46.
            · 디더가 안 보이던 이유는 **두 가지**였고, 2차에서 둘 다 반대로 갔다:
              ① effectScale 을 키웠다(10 → 16). 셰이더 주석이 정반대를 말한다 —
                 "10px 디더 블록은 뭉개져 보이고(mush) 2.5px 은 결로 읽힌다".
                 디더 격자는 이 값의 1/4 로 잡히므로 **작을수록 또렷**하다. → 5
              ② colorMode 가 gradient 였다. 그 모드는 화면 색을 그냥 계단으로
                 끊기만 한다(floor(source*steps+threshold)/steps) — 팔레트가
                 거의 검정이라 끊어도 눈에 띌 밝기 차가 없었다.
                 ink 모드는 잉크를 **얹는다**(mix(field, uInk, coverage)) —
                 바탕 색은 그대로 두고 점만 밝게 찍히므로 카드 색도 살아남는다.
            · 잉크는 기본값(#F5F5F4, 거의 흰색) 대신 한 단 낮춘 회청색을 쓴다.
              이 배경 위에 흰 글자가 얹히므로 점이 그만큼 밝으면 글자와 다툰다.

            ── 4차: 진짜 원인은 **바탕이 너무 어두운 것**이었다 ──────────────
            셰이더를 읽어 보니 디더의 점 농도는 화면 밝기에서 나온다:
              shade = floor(tone(fieldLuminance(source)) * steps + threshold) / steps
            팔레트 다섯 중 넷이 거의 검정이라(#050506 밝기 0.02 · #0E0F1A 0.06)
            fieldLuminance 가 0.05 언저리에 머물고, 그러면 위 식이 거의 모든
            픽셀에서 0 이 된다 — 잉크가 얹힐 자리가 아예 없었다. 점 크기·단계·
            색 모드를 아무리 만져도 안 보이던 이유다.

            게다가 **대비를 1.5 로 올린 것이 정확히 반대로 작용했다** — 대비는
            어두운 쪽을 더 어둡게 눌러 밝기를 더 깎는다.

            ── 5차: 밝기 대신 **팔레트**로 푼다 (선택) ──────────────────
            밝기(brightness)를 올리는 길은 화면 전체가 같이 떠서 흰 글자와 대비가
            먼저 무너진다. 대신 팔레트의 가운데 대역만 끌어올려(PALETTE 주석),
            **배경은 어둡게 두고 점이 찍힐 밝기 구간만** 만든다.
            그래서 밝기·대비는 거의 원래 값으로 되돌렸다. */}
        <GradientBackground
          config={config}
          className={styles.canvas}
          contrast={1.15}
          brightness={0.04}
          warp={1.35}
          wave={1.45}
          speed={0.46}
          effectScale={5}
          effectLevels={3}
          effectColorMode="ink"
          effectInk="#C3C9DE"
        />
      </Suspense>
    </div>
  );
}
